import validateJS from 'validate.js';
import I18n from './i18n';
import PasswordService from './password';
import config from '../config';
import {differenceInYears} from 'date-fns';

validateJS.validators.password = function (value) {
  const passwordAnalysis = PasswordService.strengthIndicators(value);
  if (passwordAnalysis.progress <= PasswordService.MIN_PROGRESS_TO_SUBMIT) {
    return passwordAnalysis.warning;
  }

  return null;
};

validateJS.validators.malaysianPostCode = function (value) {
  if (parseInt(value, 10) < 1000 || parseInt(value, 10) > 99990) {
    return I18n.t('error.validation.postCode');
  }
  return null;
};

validateJS.validators.malaysianId = function (value) {
  if (!value) return I18n.t('error.validation.idNumber');
  const isNumber = number => /^\d+$/.test(number);
  if (
    !isNumber(value.slice(0, 6)) ||
    value[6] !== '-' ||
    !isNumber(value.slice(7, 9)) ||
    value[9] !== '-' ||
    !isNumber(value.slice(10))
  ) {
    return I18n.t('error.validation.idNumber');
  }
  return null;
};

const noSpecialCharactersFormat = {
  pattern: '[0-9a-zA-ZÀ-ÿ !"#$%&\'()*+,-./:;<=>?@\\[\\]^_{|}]+|^$',
  flags: 'gi',
  message: I18n.t('error.validation.noSpecialCharacters'),
};

const onlyAlphanumericFormat = {
  pattern: '^[a-zA-Z0-9]*$',
  message: I18n.t('error.validation.noSpecialCharacters'),
};

const validateOneFieldWithLibrary = (field, constraints) => {
  const errorsList = validateJS.single(field, constraints);
  return errorsList ? errorsList[0] : false;
};

export default class ValidationService {
  static validateEmail = email => {
    return validateOneFieldWithLibrary(email, {
      presence: {
        message: I18n.t('error.validation.email'),
      },
      format: {
        pattern: config.EMAIL_REGEX,
        message: I18n.t('error.validation.email'),
      },
    });
  };

  static validatePassword(password) {
    return validateOneFieldWithLibrary(password, {
      presence: {
        message: I18n.t('error.validation.emptyPassword'),
      },
      password: {},
    });
  }

  static validateCode(code) {
    return validateOneFieldWithLibrary(code, {
      presence: {
        message: I18n.t('error.validation.code', {
          validationEmailNumberOfDigits: config.validationEmailNumberOfDigits,
        }),
      },
      length: {
        is: config.validationEmailNumberOfDigits,
        wrongLength: I18n.t('error.validation.code', {
          validationEmailNumberOfDigits: config.validationEmailNumberOfDigits,
        }),
      },
      numericality: {
        onlyInteger: true,
        notValid: I18n.t('error.validation.code', {
          validationEmailNumberOfDigits: config.validationEmailNumberOfDigits,
        }),
      },
    });
  }

  static validateName(name) {
    return validateOneFieldWithLibrary(name, {
      presence: {
        message: I18n.t('error.validation.name'),
      },
    });
  }

  static validateLocalPhoneNumber(number, trigram?: string) {
    if (trigram) {
      return validateOneFieldWithLibrary(number && number.replace(/ /g, ''), {
        presence: {
          message: I18n.t('error.validation.phoneNumber'),
        },
        format: {
          pattern: config.LOCAL_PHONE_NUMBER_REGEX[trigram],
          message: I18n.t(`error.validation.keepMyNumberFormat.${trigram}`),
        },
      });
    }
    return validateOneFieldWithLibrary(number && number.replace(/ /g, ''), {
      presence: {
        message: I18n.t('error.validation.phoneNumber'),
      },
      format: {
        pattern: config.LOCAL_PHONE_NUMBER_REGEX.all,
        message: I18n.t('error.validation.phoneNumberFormat'),
      },
    });
  }

  static validatePhoneNumber(number: ?string) {
    return validateOneFieldWithLibrary(number && number.replace(/ /g, ''), {
      presence: {
        message: I18n.t('error.validation.phoneNumber'),
      },
      format: {
        pattern: config.PHONE_NUMBER_REGEX,
        message: I18n.t('error.validation.phoneNumberLength'),
      },
    });
  }

  static validateRioNumber(RIONumber, phoneNumber) {
    if (!phoneNumber || !RIONumber) {
      return I18n.t('error.validation.rioNumberFormat');
    }

    const formattedRIONumber = RIONumber.replace(/-/g, '').toUpperCase(); // tempporary workaround to capitalize RIO

    const formattedPhoneNumber = phoneNumber.replace(/ /g, '');

    if (!formattedRIONumber.match(config.RIO_NUMBER_REGEX)) {
      return I18n.t('error.validation.rioNumberFormat');
    }

    const operatorId = formattedRIONumber.substring(0, 2);
    const typeOfContract = formattedRIONumber.substring(2, 3);
    const clientRef = formattedRIONumber.substring(3, 9);
    const validationKey = formattedRIONumber.substring(9);

    if (typeOfContract !== 'P' && typeOfContract !== 'E') {
      return I18n.t('error.validation.rioNumberFormat');
    }

    const sOrdre = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789+';
    const rioValidationKey = [0, 0, 0];
    const rioWithoutValidationKeyAndPhoneNumber =
      operatorId + typeOfContract + clientRef + formattedPhoneNumber;

    for (let n = 0; n < rioWithoutValidationKeyAndPhoneNumber.length; n++) {
      const nPos = sOrdre.indexOf(
        rioWithoutValidationKeyAndPhoneNumber.substring(n, n + 1),
      );
      rioValidationKey[0] = (rioValidationKey[0] + nPos) % sOrdre.length;
      rioValidationKey[1] = (2 * rioValidationKey[1] + nPos) % sOrdre.length;
      rioValidationKey[2] = (4 * rioValidationKey[2] + nPos) % sOrdre.length;
    }

    const validationKeyGenerateFromGivenInfos =
      sOrdre.substring(rioValidationKey[0], rioValidationKey[0] + 1) +
      sOrdre.substring(rioValidationKey[1], rioValidationKey[1] + 1) +
      sOrdre.substring(rioValidationKey[2], rioValidationKey[2] + 1);

    if (validationKey !== validationKeyGenerateFromGivenInfos) {
      return I18n.t('error.validation.rioNumberFormat');
    }

    return false;
  }

  static validateMalaysianPostCode(postCode) {
    return validateOneFieldWithLibrary(postCode, {
      presence: {
        message: I18n.t('error.validation.postCode'),
      },
      length: {is: 5, wrongLength: I18n.t('error.validation.postCode')},
      numericality: {
        onlyInteger: true,
        notValid: I18n.t('error.validation.postCode'),
      },
      malaysianPostCode: {},
    });
  }

  static validatePostCode(postCode) {
    return (
      validateOneFieldWithLibrary(postCode, {
        presence: {
          message: I18n.t('error.validation.required'),
        },
        format: {
          pattern: config.ONLY_NUMBER_POST_CODE_REGEX,
          message: I18n.t('error.validation.validPostCode'),
        },
      }) ||
      validateOneFieldWithLibrary(postCode, {
        format: {
          pattern: config.VALID_REGION_POST_CODE_REGEX,
          message: I18n.t('error.validation.validRegionPostCode'),
        },
      })
    );
  }

  static validateBillingAddressField(field, options = {}) {
    return this.validateAddressField(field, {maxLength: 65, ...options});
  }

  static validateAddressField(field, options = {}) {
    const {maxLength} = options;
    return validateOneFieldWithLibrary(field, {
      length: maxLength && {
        maximum: maxLength,
        tooLong: I18n.t('error.validation.maxLength', {length: maxLength}),
      },
      presence: !options.presenceNotRequired && {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
    });
  }

  static validateDeliveryStateOrProvince(state) {
    const maxLength = 50;
    return validateOneFieldWithLibrary(state, {
      presence: {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
      length: {
        maximum: maxLength,
        tooLong: I18n.t('error.validation.maxLength', {length: maxLength}),
      },
      format: noSpecialCharactersFormat,
    });
  }

  static validateMalaysianId(idNumber) {
    return validateOneFieldWithLibrary(idNumber, {
      presence: {
        message: I18n.t('error.validation.idNumber'),
      },
      length: {is: 14, wrongLength: I18n.t('error.validation.idNumber')},
      malaysianId: {},
    });
  }
 
  static validatePresence(field) {
    return validateOneFieldWithLibrary(field, {
      presence: {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
    });
  }

  static validatebirthDate(birthDate) {
    if (!birthDate) {
      return I18n.t('error.validation.required');
    }
    const locale = I18n.locale;
    const dateInArray = birthDate.split('/');

    const isValidDate = () => {
      if (dateInArray.length === 3) {
        const day = locale === 'fr' ? dateInArray[0] : dateInArray[1];
        const month = locale === 'fr' ? dateInArray[1] : dateInArray[0];
        const year = dateInArray[2];

        if (
          day <= 31 &&
          day > 0 &&
          day.length === 2 &&
          month <= 12 &&
          month > 0 &&
          month.length === 2 &&
          year.length === 4
        ) {
          return true;
        }
      }

      return false;
    };

    if (isValidDate()) {
      let age = 0;
      if (locale === 'fr') {
        age = differenceInYears(
          new Date(),
          new Date(
            `${dateInArray[2]}`,
            `${dateInArray[1]}`,
            `${dateInArray[0]}`,
          ),
        );
      } else {
        age = differenceInYears(
          new Date(),
          new Date(
            `${dateInArray[2]}`,
            `${dateInArray[0]}`,
            `${dateInArray[1]}`,
          ),
        );
      }

      if (age >= 18) {
        return false;
      }
      return I18n.t('error.validation.tooYoung', {ageLimit: config.ageLimit});
    }
    return I18n.t('error.validation.invalideDateFormat');
  }

  static validateSIMNumber(number) {
    return validateOneFieldWithLibrary(number, {
      presence: {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
      length: {
        is: config.simSerialNumberLength,
        wrongLength: I18n.t('error.validation.length', {
          length: config.simSerialNumberLength,
        }),
      },
    });
  }

  static validatePreferredNumbers(numbers) {
    return validateOneFieldWithLibrary(numbers, {
      presence: {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
      length: {
        minimum: 3,
        maximum: 4,
        tooLong: I18n.t('error.validation.preferredNumbers'),
        tooShort: I18n.t('error.validation.preferredNumbers'),
      },
      numericality: {
        onlyInteger: true,
        notValid: I18n.t('error.validation.preferredNumbers'),
      },
    });
  }

  static validateAddMoney(amount, minAmount, maxAmount) {
    const currency = config.currency;
    const amountWithoutComma = amount ? amount.replace(',', '.') : amount;

    return validateOneFieldWithLibrary(amountWithoutComma, {
      presence: {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
      numericality: {
        greaterThanOrEqualTo: minAmount,
        lessThanOrEqualTo: maxAmount,
        notValid: I18n.t('error.validation.numberOnly'),
        notGreaterThanOrEqualTo: I18n.t(
          'error.validation.creditWalletInvalidAmount',
          {
            currency,
            minAmount,
            maxAmount,
          },
        ),
        notLessThanOrEqualTo: I18n.t(
          'error.validation.creditWalletInvalidAmount',
          {
            currency,
            minAmount,
            maxAmount,
          },
        ),
      },
      format: {
        pattern: '^[0-9]+(.[0-9]{1,2})?$',
        message: I18n.t('error.validation.twoDecimal'),
      },
    });
  }

  static validatePromotionCode(code) {
    return validateOneFieldWithLibrary(code, {
      presence: {
        message: I18n.t('error.validation.promotionCode'),
        allowEmpty: false,
      },
    });
  }

  static validateVoucherCode(code) {
    return validateOneFieldWithLibrary(code, {
      presence: {
        message: I18n.t('error.validation.required'),
        allowEmpty: false,
      },
      format: onlyAlphanumericFormat,
    });
  }
}
